import React, { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { useTheme } from '../contexts/ThemeContext';
import { CalendarDayDetails } from './CalendarDayDetails';
import GuestListVenueShare from './GuestListVenueShare';
import ContactsDayShare from './ContactsDayShare';
import {
  checkShareAccess,
  getFirestoreDb,
  subscribeShareByToken,
} from '../utils/calendarShareService';
import { readPathSegmentAfter } from '../utils/webRouteParams';

export default function CalendarShareView({ route }) {
  const token = String(route?.params?.token || readPathSegmentAfter('share') || '').trim();
  const { theme } = useTheme();
  const navigation = useNavigation();
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const db = getFirestoreDb();
    const unsub = subscribeShareByToken(db, token, (data) => {
      setResolved(data);
      setLoading(false);
    });
    return unsub;
  }, [token]);

  const viewer = {
    uid: auth.currentUser?.uid || '',
    email: auth.currentUser?.email || '',
  };

  const access = resolved?.link ? checkShareAccess(resolved.link, viewer) : { allowed: false, reason: 'not_found' };

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.background.primary }]}>
        <ActivityIndicator size="large" color={theme.brand.accent} />
      </View>
    );
  }

  if (!resolved?.link || resolved.link.type !== 'day') {
    return (
      <View style={[styles.centered, { backgroundColor: theme.background.primary, padding: 24 }]}>
        <Text style={[styles.title, { color: theme.text.primary }]}>Link not found</Text>
        <Text style={[styles.body, { color: theme.text.secondary }]}>
          This share link is invalid or has been removed.
        </Text>
        <TouchableOpacity onPress={() => navigation.navigate('MyCalendar')} style={styles.btn}>
          <Text style={styles.btnText}>Go to calendar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (resolved.link.isActive === false) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.background.primary, padding: 24 }]}>
        <Text style={[styles.title, { color: theme.text.primary }]}>Link deactivated</Text>
        <Text style={[styles.body, { color: theme.text.secondary }]}>
          The tour owner has turned off this share link.
        </Text>
      </View>
    );
  }

  if (!access.allowed) {
    const messages = {
      sign_in_required: 'Sign in with an invited account to view this shared day.',
      not_invited: 'Your account does not have access to this shared day. Ask the tour owner for an invite.',
    };
    return (
      <View style={[styles.centered, { backgroundColor: theme.background.primary, padding: 24 }]}>
        <Text style={[styles.title, { color: theme.text.primary }]}>Access required</Text>
        <Text style={[styles.body, { color: theme.text.secondary }]}>
          {messages[access.reason] || 'You cannot view this shared link.'}
        </Text>
        {access.reason === 'sign_in_required' ? (
          <TouchableOpacity
            onPress={() => navigation.navigate('Login', {
              message: 'Sign in with an invited account to view this shared day.',
              returnTo: 'CalendarShareView',
              returnParams: { token },
            })}
            style={styles.btn}
          >
            <Text style={styles.btnText}>Sign in</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  const modules = (resolved.link.modules || []).join(',');
  const moduleList = (resolved.link.modules || []).map((m) => String(m || '').trim()).filter(Boolean);
  const guestListOnly = moduleList.length === 1 && moduleList[0] === 'GUEST LIST';
  if (guestListOnly) {
    return <GuestListVenueShare ownerId={resolved.ownerId} link={resolved.link} />;
  }
  const contactsOnly = moduleList.length === 1 && moduleList[0] === 'CONTACTS';
  if (contactsOnly) {
    return <ContactsDayShare ownerId={resolved.ownerId} link={resolved.link} />;
  }
  return (
    <CalendarDayDetails
      route={{
        params: {
          dateKey: resolved.link.dateKey,
          uid: resolved.ownerId,
          tourId: resolved.link.tourId,
          tour: resolved.link.tourId,
          modules,
          shareToken: token,
        },
      }}
    />
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 20, fontWeight: '800', marginBottom: 8, textAlign: 'center' },
  body: { fontSize: 14, lineHeight: 20, textAlign: 'center', marginBottom: 16 },
  btn: {
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#27ae60',
  },
  btnText: { color: '#fff', fontWeight: '800', fontSize: 15 },
});
